import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { ACTING_AS_HEADER } from '@doorly/shared';
import { getActingAsUserId } from './actingAs';
import { getImpersonatedUserId } from './impersonation';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

function buildQuery(params?: Record<string, string | number | undefined | null>): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

/**
 * Returns a typed fetch helper bound to the current Clerk session. The token is
 * fetched lazily per request so Clerk can refresh behind the scenes.
 */
export function useApi() {
  const { getToken } = useAuth();

  async function fetchWithToken<T>(
    path: string,
    params?: Record<string, string | number | undefined | null>,
    forceRefresh = false,
  ): Promise<T> {
    const token = await getToken(forceRefresh ? { skipCache: true } : undefined);
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const imp = getImpersonatedUserId();
    if (imp) headers['X-Impersonate-As'] = imp;
    // Session PIN : les lectures des écrans partent au nom du vendeur actif.
    const acting = getActingAsUserId();
    if (acting) headers[ACTING_AS_HEADER] = acting;
    const res = await fetch(`${API_URL}${path}${buildQuery(params)}`, { headers });
    // On 401, retry ONCE with a fresh token (Clerk cache may hold an expired JWT)
    if (res.status === 401 && !forceRefresh) {
      return fetchWithToken<T>(path, params, true);
    }
    if (!res.ok) {
      if (res.status === 401) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      const body = await res.text().catch(() => '');
      const err = new Error(`${res.status} ${res.statusText} — ${body}`) as Error & { status: number };
      err.status = res.status;
      throw err;
    }
    return res.json() as Promise<T>;
  }

  return {
    get: <T>(path: string, params?: Record<string, string | number | undefined | null>) =>
      fetchWithToken<T>(path, params),
  };
}

export function useApiQuery<T>(
  key: readonly unknown[],
  path: string | null | false,
  params?: Record<string, string | number | undefined | null>,
) {
  const api = useApi();
  return useQuery<T>({
    queryKey: [...key, params],
    // `path` à null/false désactive la requête — utile quand la fetch
    // dépend d'un paramètre optionnel (e.g. un managerId nullable).
    enabled: !!path,
    queryFn: () => api.get<T>(path as string, params),
  });
}
